import { Problem } from '../types/Problem';

interface QuestionRequest {
  difficulty: 'Easy' | 'Medium' | 'Hard' | null;
  area:
    | 'Arrays & Strings'
    | 'Trees & Graphs'
    | 'Dynamic Programming'
    | 'Data Structures'
    | 'Algorithms'
    | null;
  company_style: 'Meta' | 'Google' | 'Amazon' | 'Microsoft' | 'Apple' | null;
}

export const generateQuestion = async (preferences: {
  difficulty: string;
  area: string;
  companyStyle: string;
}, signal?: AbortSignal): Promise<Problem> => {
  try {

    const requestBody: QuestionRequest = {
      difficulty: preferences.difficulty
        ? (preferences.difficulty as 'Easy' | 'Medium' | 'Hard')
        : null,
      area: preferences.area
        ? (preferences.area as
            | 'Arrays & Strings'
            | 'Trees & Graphs'
            | 'Dynamic Programming'
            | 'Data Structures'
            | 'Algorithms')
        : null,
      company_style: preferences.companyStyle
        ? (preferences.companyStyle as
            | 'Meta'
            | 'Google'
            | 'Amazon'
            | 'Microsoft'
            | 'Apple')
        : null,
    };

    const baseURL = process.env.NODE_ENV === 'development' ? 'http://localhost:8000' : 'https://stronghireai.com';
    const response = await fetch(
      `${baseURL}/openai_api/generate_question/`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include', // Include JWT token from cookies
        body: JSON.stringify(requestBody),
        signal,
      }
    );

    if (!response.ok) {
      let errorMessage = 'Failed to generate question';

      try {
        const errorData = await response.json();
        if (response.status === 500 && errorData.error?.includes('API key')) {
          errorMessage =
            'OpenAI API key not configured. Please contact support.';
        } else if (response.status === 400) {
          errorMessage = errorData.error || 'Invalid request format';
        } else if (response.status === 500) {
          errorMessage = errorData.error || 'Server error occurred';
        } else {
          errorMessage =
            errorData.error ||
            `Error ${response.status}: ${response.statusText}`;
        }
      } catch (jsonError) {
        errorMessage = `HTTP ${response.status}: ${response.statusText}`;
      }

      throw new Error(errorMessage);
    }

    const data = await response.json();

    return data;
  } catch (error) {
    console.error('Error generating question:', error);

    if (error instanceof TypeError && error.message.includes('fetch')) {
      throw new Error(
        'Unable to connect to server. Please check your connection and try again.'
      );
    }

    throw error;
  }
};

export const generateFreeFormQuestion = async (
  freeFormText: string,
  signal?: AbortSignal
): Promise<Problem> => {
  try {

    const baseURL = process.env.NODE_ENV === 'development' ? 'http://localhost:8000' : 'https://stronghireai.com';
    const response = await fetch(
      `${baseURL}/openai_api/generate_freeform_question/`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include', // Include JWT token from cookies
        body: JSON.stringify({
          request: freeFormText,
        }),
        signal,
      }
    );

    if (!response.ok) {
      let errorMessage = 'Failed to generate free-form question';

      try {
        const errorData = await response.json();
        if (response.status === 500 && errorData.error?.includes('API key')) {
          errorMessage =
            'OpenAI API key not configured. Please contact support.';
        } else if (response.status === 400) {
          errorMessage = errorData.error || 'Invalid request format';
        } else if (response.status === 500) {
          errorMessage = errorData.error || 'Server error occurred';
        } else {
          errorMessage =
            errorData.error ||
            `Error ${response.status}: ${response.statusText}`;
        }
      } catch (jsonError) {
        errorMessage = `HTTP ${response.status}: ${response.statusText}`;
      }

      throw new Error(errorMessage);
    }

    const data = await response.json();

    return data;
  } catch (error) {
    console.error('Error generating free-form question:', error);

    if (error instanceof TypeError && error.message.includes('fetch')) {
      throw new Error(
        'Unable to connect to server. Please check your connection and try again.'
      );
    }

    throw error;
  }
};
